import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { environment } from '~environments/environment';
import { FracMine, Mine } from '~lmo/models/mine.model';
import { ErrorHandlingService } from '~services/error-handling.service';
import { RouterStateService } from '~services/router-state.service';
import * as fromRouterConstants from '../lmo-routing.constants';

export interface Loader {
  id: number;
}

export interface MineCloseTime {
  daysSinceSunday: number;
  closeMinuteOfDay: number;
  openMinuteOfDay: number;
}

@Injectable({
  providedIn: 'root',
})
export class LmoLoadersService {
  private loaders$$: BehaviorSubject<Mine[]> = new BehaviorSubject<Mine[]>(null);
  private inUserLoaders$$: BehaviorSubject<Mine[]> = new BehaviorSubject<Mine[]>(null);
  private mineClosedTimes$$ = new BehaviorSubject<{ [key: number]: MineCloseTime[] }>({});

  public get loaders$(): Observable<Mine[]> {
    return this.loaders$$.asObservable();
  }

  public get inUseLoaders$(): Observable<Mine[]> {
    return this.inUserLoaders$$.asObservable();
  }

  public get mineClosedTimes$(): Observable<MineCloseTime[]> {
    return combineLatest(
      this.mineClosedTimes$$.asObservable(),
      this.routerState.listenForParamChange$(fromRouterConstants.LOADER_CLOSED_HOURS_ID),
    ).pipe(
      map(([closedTimes, mineId]) => {
        if (closedTimes && mineId && closedTimes[mineId]) {
          return closedTimes[mineId];
        }
        return [];
      }),
    );
  }

  constructor(
    private http: HttpClient,
    private routerState: RouterStateService,
    private snackbar: MatSnackBar,
    private error: ErrorHandlingService,
  ) {
    this.listenForLoaderClosedTimesId();
    this.http.get<Mine[]>(`${environment.api}/mine`).subscribe((loaders) => {
      this.loaders$$.next(loaders);
    });

    this.loaders$$.subscribe((loaders) => {
      if (loaders) {
        const inUseLoaders = loaders.filter((loader) => loader.inUse);
        this.inUserLoaders$$.next(inUseLoaders);
      }
    });
  }

  public getSingleLoader$(mineId: number): Observable<FracMine> {
    return this.http.get<FracMine>(`${environment.api}/mine/${mineId}`);
  }

  public async setMineClosedTimes(times: MineCloseTime[]) {
    if (!validateMineCloseTimes(times)) {
      this.error.showError('Open times must be after close times');
      return;
    }
    try {
      const loaderId = await this.routerState
        .listenForParamChange$(fromRouterConstants.LOADER_CLOSED_HOURS_ID)
        .pipe(take(1))
        .toPromise();
      const updatedTimes = await this.http
        .post<MineCloseTime[]>(`${environment.api}/mine/${loaderId}/closed_hours`, times)
        .toPromise();
      this.mineClosedTimes$$.next({
        ...this.mineClosedTimes$$.value,
        [loaderId]: updatedTimes,
      });
      this.snackbar.open('Non-Operational Hours Updated', null, { duration: 2000 });
    } catch (err) {
      console.error(err);
      this.error.showError(err);
    }
  }

  private listenForLoaderClosedTimesId() {
    this.routerState
      .listenForParamChange$(fromRouterConstants.LOADER_CLOSED_HOURS_ID)
      .pipe(
        switchMap((mineId) => {
          if (mineId) {
            return this.http.get<MineCloseTime[]>(`${environment.api}/mine/${mineId}/closed_hours`).pipe(
              map((times) => {
                this.mineClosedTimes$$.next({
                  ...this.mineClosedTimes$$.value,
                  [mineId]: times,
                });
              }),
            );
          }
          return of(null as any);
        }),
      )
      .subscribe();
  }
}

function validateMineCloseTimes(times: MineCloseTime[]): boolean {
  return times.every((time) => time.openMinuteOfDay > time.closeMinuteOfDay);
}
